import { FormEvent, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import Input from "../components/UI/Input";
import ValidatedInput from "../components/UI/ValidatedInput";
import ModelErrors from "../models/pageModels/ModelErrors";
import EditOrganizationPageModel from "../models/pageModels/EditOrganizationPageModel";
import { getDefaultErrors } from "../utils/modelHelpers";
import { ConfiguredRequiredField, getOrganizationConfiguredRequiredFields, updateOrganization, updateOrganizationConfiguredRequiredField } from "../services/organizationService";
import { mapOrganizationToEditOrganizationPageModel } from "../mappers/organizationMapper";
import BackButton from "../components/UI/BackButton";
import Loader from "../components/UI/Loader";
import ValidatedSelect from "../components/UI/ValidatedSelect";
import states from "../constants/states";
import { OrganizationContext } from "../App";
import { isValidEmail } from "../utils/validation/emailValidation";
import Checkbox from "../components/UI/Checkbox";
import { clearReferenceDataCache } from "../services/Referencedataservice";
import { remove } from "../utils/cacheData";
import { removeSuffix } from "../services/cacheService";
import { clearConfiguredRequiredFieldsCache } from "../services/clientsService";
import { configuredRequiredFieldsOrgSettingsEnabled, demographicsOrgSettingsEnabled, householdMemberDemographicsReportsEnabled, organizationProfileFieldConfigurationEnabled } from "../utils/features";
import DemographicsPreviewModal from "../components/Organization/DemographicsPreviewModal";
import DemographicsOptionsInfoModal from "../components/Organization/DemographicsOptionsInfoModal";
import WarningMessages from "../components/UI/WarnginMessages";
import { formatJSONDateWithoutTime } from "../utils/dateHelpers";

const getDefaultModel = (): EditOrganizationPageModel => {
    return {
        organizationName: '',
        organizationUrl: '',
        organizationEmail: '',
        organizationPhoneNumber: '',
        organizationAddressLine1: '',
        organizationAddressLine2: '',
        organizationCity: '',
        organizationState: '',
        organizationZipCode: '',
        visiblePronouns: true,
        visibleGender: true,
        visibleEthnicity: true,
        visibleRace: true,
        visibleAddress: true,
        visibleCity: true,
        visibleState: true,
        visibleZip: true,
        visiblePhone: true,
        visibleEmail: true,
        demographicsGenderMode: 'current',
        demographicsRaceMode: 'separate',
        householdMemberParticipantMode: 'defaultUnchecked',
        dateOfBirthRequired: false,
        dateOfBirthRequiredEffectiveDate: '',
        raceRequired: false,
        ethnicityRequired: false,
        genderRequired: false,
        pronounsRequired: false,
        addressRequired: false,
        cityRequired: false,
        stateRequired: false,
        zipRequired: false,
        phoneRequired: false,
        emailRequired: false
    };
}

const applyRequiredFieldsToModel = (model: EditOrganizationPageModel, requiredFields: ConfiguredRequiredField[]): EditOrganizationPageModel => {
    const dateOfBirthField = requiredFields.find(field => field.fieldName === 'dateOfBirth');
    const isRequired = (fieldName: string) => requiredFields.some(field => field.fieldName === fieldName);

    return {
        ...model,
        dateOfBirthRequired: !!dateOfBirthField,
        dateOfBirthRequiredEffectiveDate: dateOfBirthField?.effectiveDate || '',
        raceRequired: isRequired('race'),
        ethnicityRequired: isRequired('ethnicity'),
        genderRequired: isRequired('gender'),
        pronounsRequired: isRequired('pronouns'),
        addressRequired: isRequired('address'),
        cityRequired: isRequired('city'),
        stateRequired: isRequired('state'),
        zipRequired: isRequired('zip'),
        phoneRequired: isRequired('phone'),
        emailRequired: isRequired('email')
    };
};

const profileRequirementSettings: Array<{ fieldName: string; modelField: keyof EditOrganizationPageModel; label: string }> = [
    { fieldName: 'pronouns', modelField: 'pronounsRequired', label: 'Preferred Pronouns' },
    { fieldName: 'gender', modelField: 'genderRequired', label: 'Gender' },
    { fieldName: 'ethnicity', modelField: 'ethnicityRequired', label: 'Ethnicity' },
    { fieldName: 'race', modelField: 'raceRequired', label: 'Race' },
    { fieldName: 'address', modelField: 'addressRequired', label: 'Address' },
    { fieldName: 'city', modelField: 'cityRequired', label: 'City' },
    { fieldName: 'state', modelField: 'stateRequired', label: 'State' },
    { fieldName: 'zip', modelField: 'zipRequired', label: 'Zip' },
    { fieldName: 'phone', modelField: 'phoneRequired', label: 'Phone' },
    { fieldName: 'email', modelField: 'emailRequired', label: 'Email' }
];

const profileVisibilityFields: Record<string, keyof EditOrganizationPageModel> = {
    race: 'visibleRace',
    ethnicity: 'visibleEthnicity',
    gender: 'visibleGender',
    pronouns: 'visiblePronouns',
    address: 'visibleAddress',
    city: 'visibleCity',
    state: 'visibleState',
    zip: 'visibleZip',
    phone: 'visiblePhone',
    email: 'visibleEmail'
};

const EditOrganization = () => {
    const [model, setModel] = useState<EditOrganizationPageModel>();
    const [modelErrors, setModelErrors] = useState<ModelErrors>(getDefaultErrors(getDefaultModel()));
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [apiErrors, setApiErrors] = useState<any>();
    const [loadingRequiredFields, setLoadingRequiredFields] = useState(false);
    const [showDemographicsOptionsInfo, setShowDemographicsOptionsInfo] = useState(false);
    const [showDemographicsPreview, setShowDemographicsPreview] = useState(false);
    const [configurationWarnings, setConfigurationWarnings] = useState<string[]>([]);
    const navigate = useNavigate();
    const organization = useContext(OrganizationContext);

    useEffect(() => {
        document.title = "Edit Organization"
    }
        , []);

    useEffect(() => {
        if (organization.loading) {
            return;
        }

        if (organization.error) {
            setApiErrors(organization.error);
        }

        if (organization.value) {
            const organizationModel = mapOrganizationToEditOrganizationPageModel(organization.value);

            if (!configuredRequiredFieldsOrgSettingsEnabled) {
                setModel(organizationModel);
                return;
            }

            setLoadingRequiredFields(true);
            getOrganizationConfiguredRequiredFields().then(requiredFields => {
                setModel(applyRequiredFieldsToModel(organizationModel, requiredFields));
            }).catch(e => {
                console.error(e);
                setApiErrors(e);
                setModel(organizationModel);
            }).finally(() => {
                setLoadingRequiredFields(false);
            });
        }
    }, [organization]);

    const handleOnChange = (fieldName: string, value: any) => {
        setModel({
            ...model!,
            [fieldName]: value
        });
    };

    const addConfigurationWarning = (message: string) => {
        setConfigurationWarnings(current => current.includes(message) ? current : [...current, message]);
    };

    const handleVisibilityChange = (fieldName: string, visible: boolean) => {
        if (!model) {
            return;
        }

        const requirement = profileRequirementSettings.find(setting => setting.fieldName === fieldName);
        const updates: Record<string, boolean> = {
            [profileVisibilityFields[fieldName]]: visible
        };

        if (!visible && requirement && Boolean(model[requirement.modelField])) {
            updates[requirement.modelField] = false;
            addConfigurationWarning(`${requirement.label} was made optional because it is hidden.`);
        }

        setModel({ ...model, ...updates });
    };

    const handleRequiredFieldChange = (fieldName: string, required: boolean) => {
        if (!model) {
            return;
        }

        const requirement = profileRequirementSettings.find(setting => setting.fieldName === fieldName);
        if (!requirement) {
            return;
        }

        const updates: Record<string, boolean> = {
            [requirement.modelField]: required
        };
        const visibilityField = profileVisibilityFields[fieldName];

        if (required && !Boolean(model[visibilityField])) {
            updates[visibilityField] = true;
            addConfigurationWarning(`${requirement.label} was made visible because it is required.`);
        }

        setModel({ ...model, ...updates });
    };

    const handleRaceModeChange = (raceMode: 'separate' | 'combined') => {
        if (!model) {
            return;
        }

        const updates: Partial<EditOrganizationPageModel> = { demographicsRaceMode: raceMode };
        if (raceMode === 'combined' && model.ethnicityRequired) {
            updates.ethnicityRequired = false;
            addConfigurationWarning('Ethnicity was made optional because the combined Race and Ethnicity question uses the Race requirement.');
        }

        setModel({ ...model, ...updates });
    };

    const validate = () => {
        if (!model) {
            return false;
        }

        const newErrors = getDefaultErrors(getDefaultModel());
        let isValid = true;

        if (!model.organizationName) {
            isValid = false;
            newErrors.organizationName.push('Name is required');
        }

        if (!model.organizationEmail) {
            isValid = false;
            newErrors.organizationEmail.push('Email is required');
        } else if (!isValidEmail(model.organizationEmail)) {
            isValid = false;
            newErrors.organizationEmail.push('Email is not a valid format. Must be in the format name@example.com');
        }

        if (!model.organizationPhoneNumber) {
            isValid = false;
            newErrors.organizationPhoneNumber.push('Phone Number is required');
        }

        if (!model.organizationAddressLine1) {
            isValid = false;
            newErrors.organizationAddressLine1.push('Address Line 1 is required');
        }

        if (!model.organizationCity) {
            isValid = false;
            newErrors.organizationCity.push('City is required');
        }

        if (!model.organizationState) {
            isValid = false;
            newErrors.organizationState.push('State is required');
        }

        if (!model.organizationZipCode) {
            isValid = false;
            newErrors.organizationZipCode.push('Zip is required');
        }

        setModelErrors({ ...newErrors });

        return isValid;
    };

    const handleOnSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!model) {
            return;
        }

        setApiErrors(undefined);
        setSubmitted(true);
        if (!validate()) {
            return;
        }
        const reconciledModel = {
            ...model,
            ...Object.fromEntries(profileRequirementSettings.map(setting => [
                setting.modelField,
                setting.fieldName === 'ethnicity' && model.demographicsRaceMode === 'combined'
                    ? false
                    : Boolean(model[profileVisibilityFields[setting.fieldName]]) && Boolean(model[setting.modelField])
            ]))
        } as EditOrganizationPageModel;

        setSubmitted(false);
        setSubmitting(true);
        const profileConfigurationEnabled = organizationProfileFieldConfigurationEnabled;

        updateOrganization({
            name: reconciledModel.organizationName,
            email: reconciledModel.organizationEmail,
            phoneNumber: reconciledModel.organizationPhoneNumber,
            addressLine1: reconciledModel.organizationAddressLine1,
            addressLine2: reconciledModel.organizationAddressLine2,
            city: reconciledModel.organizationCity,
            state: reconciledModel.organizationState,
            zipCode: reconciledModel.organizationZipCode,
            url: reconciledModel.organizationUrl,
            ...(demographicsOrgSettingsEnabled ? {
                demographicsVisibleFields: [
                    reconciledModel.visiblePronouns ? 'pronouns' : '',
                    reconciledModel.visibleGender ? 'gender' : '',
                    reconciledModel.visibleEthnicity ? 'ethnicity' : '',
                    reconciledModel.visibleRace ? 'race' : '',
                    reconciledModel.visibleAddress ? 'address' : '',
                    reconciledModel.visibleCity ? 'city' : '',
                    reconciledModel.visibleState ? 'state' : '',
                    reconciledModel.visibleZip ? 'zip' : '',
                    reconciledModel.visiblePhone ? 'phone' : '',
                    reconciledModel.visibleEmail ? 'email' : ''
                ].filter(v => !!v),
                demographicsGenderMode: reconciledModel.demographicsGenderMode,
                demographicsRaceMode: reconciledModel.demographicsRaceMode
            } : {}),
            ...(householdMemberDemographicsReportsEnabled ? {
                householdMemberParticipantMode: reconciledModel.householdMemberParticipantMode
            } : {})
        }).then(() => {
            if (!configuredRequiredFieldsOrgSettingsEnabled) {
                return;
            }

            const updates = [updateOrganizationConfiguredRequiredField({
                fieldName: 'dateOfBirth',
                enabled: reconciledModel.dateOfBirthRequired,
                effectiveDate: reconciledModel.dateOfBirthRequired
                    ? reconciledModel.dateOfBirthRequiredEffectiveDate || null
                    : null
            })];

            if (profileConfigurationEnabled) {
                updates.push(...profileRequirementSettings.map(setting => updateOrganizationConfiguredRequiredField({
                    fieldName: setting.fieldName,
                    enabled: Boolean(reconciledModel[setting.modelField])
                })));
            }

            return Promise.all(updates);
        }).then(() => {
            clearReferenceDataCache();
            clearConfiguredRequiredFieldsCache();
            remove('genders');
            remove('races');
            remove('ethnicities');
            removeSuffix('-search-fields-v5');
            removeSuffix('-search-filters-v3');
            organization.refresh();
            navigate(-1);
        }).catch(e => {
            console.error(e);
            setApiErrors(e);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    if (organization.loading || loadingRequiredFields || !model) {
        return <Loader />
    }

    const demographicsPreviewConfig = {
        demographicsVisibleFields: [
            model.visiblePronouns ? 'pronouns' : '',
            model.visibleGender ? 'gender' : '',
            model.visibleEthnicity ? 'ethnicity' : '',
            model.visibleRace ? 'race' : '',
            ...(organizationProfileFieldConfigurationEnabled ? [
                model.visibleAddress ? 'address' : '',
                model.visibleCity ? 'city' : '',
                model.visibleState ? 'state' : '',
                model.visibleZip ? 'zip' : '',
                model.visiblePhone ? 'phone' : '',
                model.visibleEmail ? 'email' : ''
            ] : [])
        ].filter(v => !!v),
        demographicsGenderMode: model.demographicsGenderMode,
        demographicsRaceMode: model.demographicsRaceMode
    };

    const demographicsOptionsChanged = model.demographicsGenderMode !== (organization.value?.demographicsGenderMode || 'current')
        || model.demographicsRaceMode !== (organization.value?.demographicsRaceMode || 'separate');
    const dateOfBirthRequirementSummary = !model.dateOfBirthRequired
        ? 'Date of Birth is not required.'
        : model.dateOfBirthRequiredEffectiveDate
            ? `Date of Birth is required for Participants and Household Members created on or after ${formatJSONDateWithoutTime(model.dateOfBirthRequiredEffectiveDate, 'MM/dd/yyyy', true)}.`
            : 'Date of Birth is required for new and edited Participants and Household Members.';
    const profileConfigurationEnabled = organizationProfileFieldConfigurationEnabled;

    return (
        <section id="main-content" tabIndex={-1}>
            <div className="section">
                <div className="row">
                    <div className="column">
                        <BackButton />
                        <h1 className="form-header">Edit Organization</h1>
                        <form onSubmit={handleOnSubmit}>
                            <ApiErrorMessages error={apiErrors} />
                            <div className="row margin-vertical-2">
                                <div className="column">
                                    <ValidatedInput id="organization-name" required={true}
                                        value={model.organizationName}
                                        onChange={(e) => handleOnChange('organizationName', e)}
                                        label="Name"
                                        messages={(submitted && modelErrors.organizationName) || []} />
                                </div>
                            </div>
                            <div className="row margin-vertical-2">
                                <div className="column-medium-4">
                                    <Input id="organization-url"
                                        value={model.organizationUrl}
                                        onChange={(e) => handleOnChange('organizationUrl', e)}
                                        label="URL" />
                                </div>
                                <div className="column-medium-4">
                                    <ValidatedInput id="organization-email" required={true}
                                        value={model.organizationEmail}
                                        onChange={(e) => handleOnChange('organizationEmail', e)}
                                        label="E-Mail"
                                        messages={(submitted && modelErrors.organizationEmail) || []} />
                                </div>
                                <div className="column-medium-4">
                                    <ValidatedInput id="organization-phone-number" required={true}
                                        value={model.organizationPhoneNumber}
                                        onChange={(e) => handleOnChange('organizationPhoneNumber', e)}
                                        label="Phone Number"
                                        mask="(000)000-0000"
                                        useMaskedValue={true}
                                        messages={(submitted && modelErrors.organizationPhoneNumber) || []} />
                                </div>
                            </div>
                            <div className="row margin-vertical-2">
                                <div className="column">
                                    <ValidatedInput id="organization-address-line-1" required={true}
                                        value={model.organizationAddressLine1}
                                        onChange={(e) => handleOnChange('organizationAddressLine1', e)}
                                        label="Address Line 1"
                                        messages={(submitted && modelErrors.organizationAddressLine1) || []} />
                                </div>
                            </div>
                            <div className="row margin-vertical-2">
                                <div className="column">
                                    <Input id="organization-address-line-2"
                                        value={model.organizationAddressLine2}
                                        onChange={(e) => handleOnChange('organizationAddressLine2', e)}
                                        label="Address Line 2" />
                                </div>
                            </div>
                            <div className="row margin-vertical-2">
                                <div className="column-medium-4">
                                    <ValidatedInput id="organization-city" required={true}
                                        value={model.organizationCity}
                                        onChange={(e) => handleOnChange('organizationCity', e)}
                                        label="City"
                                        messages={(submitted && modelErrors.organizationCity) || []} />
                                </div>
                                <div className="column-medium-4">
                                    <ValidatedSelect id="organization-state" required={true}
                                        value={model.organizationState}
                                        onChange={(e) => handleOnChange('organizationState', e)}
                                        label="State"
                                        messages={(submitted && modelErrors.organizationState) || []}>
                                        <option value="">Select One</option>
                                        {states.map((s, i) => {
                                            return <option key={i} value={s.value}>{s.label}</option>
                                        })}
                                    </ValidatedSelect>
                                </div>
                                <div className="column-medium-4">
                                    <ValidatedInput id="organization-zip" required={true}
                                        value={model.organizationZipCode}
                                        onChange={(e) => handleOnChange('organizationZipCode', e)}
                                        label="Zip"
                                        messages={(submitted && modelErrors.organizationZipCode) || []} />
                                </div>
                            </div>
                            {demographicsOrgSettingsEnabled && <>
                                <div className="row margin-top-5">
                                    <div className="column">
                                        <h2>Edit Demographic Preferences</h2>
                                        <p>Choose which standard demographics fields your organization uses and how gender and race/ethnicity should appear across forms, profiles, reports, and exports.</p>
                                    </div>
                                </div>
                                <div className="row margin-vertical-2">
                                    <div className="column">
                                        <h3>Display or Hide Standard Demographics</h3>
                                        <p>Uncheck boxes to hide the listed demographics from forms, profiles, reports, and exports. Hidden data remains stored, but it should no longer appear in active workflows.</p>
                                        <div className="row margin-top-2">
                                            <div className="column-medium-3">
                                                <Checkbox id="visible-pronouns"
                                                    checked={model.visiblePronouns}
                                                    onChange={(e) => handleVisibilityChange('pronouns', e)}
                                                    label="Pronouns" />
                                            </div>
                                            <div className="column-medium-3">
                                                <Checkbox id="visible-gender"
                                                    checked={model.visibleGender}
                                                    onChange={(e) => handleVisibilityChange('gender', e)}
                                                    label="Gender" />
                                            </div>
                                            {model.demographicsRaceMode === 'separate' && <div className="column-medium-3">
                                                <Checkbox id="visible-ethnicity"
                                                    checked={model.visibleEthnicity}
                                                    onChange={(e) => handleVisibilityChange('ethnicity', e)}
                                                    label="Ethnicity" />
                                            </div>}
                                            <div className="column-medium-3">
                                                <Checkbox id="visible-race"
                                                    checked={model.visibleRace}
                                                    onChange={(e) => handleVisibilityChange('race', e)}
                                                    label="Race" />
                                            </div>
                                        </div>
                                        {profileConfigurationEnabled && <div className="row margin-top-2">
                                            <div className="column-medium-3"><Checkbox id="visible-address" checked={model.visibleAddress} onChange={(e) => handleVisibilityChange('address', e)} label="Address" /></div>
                                            <div className="column-medium-3"><Checkbox id="visible-city" checked={model.visibleCity} onChange={(e) => handleVisibilityChange('city', e)} label="City" /></div>
                                            <div className="column-medium-3"><Checkbox id="visible-state" checked={model.visibleState} onChange={(e) => handleVisibilityChange('state', e)} label="State" /></div>
                                            <div className="column-medium-3"><Checkbox id="visible-zip" checked={model.visibleZip} onChange={(e) => handleVisibilityChange('zip', e)} label="Zip" /></div>
                                            <div className="column-medium-3"><Checkbox id="visible-phone" checked={model.visiblePhone} onChange={(e) => handleVisibilityChange('phone', e)} label="Phone" /></div>
                                            <div className="column-medium-3"><Checkbox id="visible-email" checked={model.visibleEmail} onChange={(e) => handleVisibilityChange('email', e)} label="Email" /></div>
                                        </div>}
                                        <WarningMessages messages={configurationWarnings} showIcon={true} />
                                    </div>
                                </div>
                                <div className="row margin-vertical-2">
                                    <div className="column">
                                        <h3>Choose Standard Demographics Options</h3>
                                        <p>Select your preferred gender and race/ethnicity field formats below. This determines how values are stored and displayed in participant profiles, reports, and exports.</p>
                                    </div>
                                </div>
                                <div className="row margin-vertical-2">
                                    <div className="column-medium-6">
                                        <ValidatedSelect id="demographics-gender-mode"
                                            value={model.demographicsGenderMode}
                                            onChange={(e) => handleOnChange('demographicsGenderMode', e)}
                                            label="Gender Options"
                                            messages={[]}>
                                            <option value="current">Default Options</option>
                                            <option value="simplified">Simplified: Male, Female, Other</option>
                                        </ValidatedSelect>
                                    </div>
                                    <div className="column-medium-6">
                                        <ValidatedSelect id="demographics-race-mode"
                                            value={model.demographicsRaceMode}
                                            onChange={(e) => handleRaceModeChange(e as 'separate' | 'combined')}
                                            label="Race and Ethnicity Options"
                                            messages={[]}>
                                            <option value="separate">Default: Separate Race and Ethnicity questions</option>
                                            <option value="combined">OMB-Aligned: Combined Race/Ethnicity question</option>
                                        </ValidatedSelect>
                                    </div>
                                </div>
                                {demographicsOptionsChanged && <div className="row">
                                    <div className="column">
                                        <WarningMessages messageClassName="prominent" showIcon={true}>
                                            <span>
                                                Changing gender or race/ethnicity options will <strong>permanently alter</strong> any existing participants to match your chosen options. Some changes are easily reversed, but others are not - especially for "Other" Gender categories and Ethnicity.
                                            </span>
                                        </WarningMessages>
                                    </div>
                                </div>}
                                <div className="row justify-content-end margin-top-3">
                                    <div className="column-auto">
                                        <Button id="see-demographics-differences-button"
                                            type="button"
                                            className="secondary"
                                            text="See Differences"
                                            onClick={() => setShowDemographicsOptionsInfo(true)} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id="preview-demographics-button"
                                            type="button"
                                            className="secondary"
                                            text="Preview Demographic Fields"
                                            onClick={() => setShowDemographicsPreview(true)} />
                                    </div>
                                </div>
                            </>}
                            {configuredRequiredFieldsOrgSettingsEnabled && <>
                                <div className="row margin-top-5">
                                    <div className="column">
                                        <h2>Required Participant Fields</h2>
                                        <p>Choose whether standard participant fields must be completed before participant and household member records can be saved.</p>
                                        <h3>Date of Birth</h3>
                                    </div>
                                </div>
                                <div className="row margin-vertical-2">
                                    <div className="column">
                                        <p className="text-bold">{dateOfBirthRequirementSummary}</p>
                                        <Checkbox id="date-of-birth-required"
                                            checked={model.dateOfBirthRequired}
                                            onChange={(e) => handleOnChange('dateOfBirthRequired', e)}
                                            label="Require Date of Birth"
                                            containerClassName="unboxed compact-checkbox" />
                                    </div>
                                </div>
                                {model.dateOfBirthRequired && <div className="row margin-vertical-2">
                                    <div className="column-medium-6">
                                        <ValidatedInput id="date-of-birth-required-effective-date"
                                            type="date"
                                            value={model.dateOfBirthRequiredEffectiveDate}
                                            onChange={(e) => handleOnChange('dateOfBirthRequiredEffectiveDate', e)}
                                            label="Start requiring Date of Birth for records created on or after"
                                            messages={(submitted && modelErrors.dateOfBirthRequiredEffectiveDate) || []} />
                                    </div>
                                    <div className="column-medium-6">
                                        <p className="text-sub margin-top-4">
                                            Leave this blank to require Date of Birth for new and edited Participants and Household Members. Participants and Household Members created before this date can still be saved without Date of Birth. Anyone created on or after this date must have Date of Birth completed.
                                        </p>
                                    </div>
                                </div>}
                                {profileConfigurationEnabled && <div className="row margin-top-3">
                                    <div className="column">
                                        <h3>Additional Required Fields</h3>
                                        <p>Required fields apply to participant saves, imports, merges, and public submissions.</p>
                                        <div className="row margin-top-2">
                                            {profileRequirementSettings.filter(setting =>
                                                setting.fieldName !== 'ethnicity' || model.demographicsRaceMode === 'separate'
                                            ).map(setting => <div className="column-medium-4" key={setting.fieldName}>
                                                <Checkbox id={`${setting.fieldName}-required`}
                                                    checked={Boolean(model[setting.modelField])}
                                                    onChange={(checked) => handleRequiredFieldChange(setting.fieldName, checked)}
                                                    label={`Require ${setting.label}`}
                                                    containerClassName="unboxed compact-checkbox" />
                                            </div>)}
                                        </div>
                                    </div>
                                </div>}
                            </>}
                            {householdMemberDemographicsReportsEnabled && <>
                            <div className="row margin-top-5">
                                <div className="column">
                                    <h2>Household Member Participation</h2>
                                    <p>Choose how new household members are handled when they are added to a participant's household.</p>
                                </div>
                            </div>
                            <div className="row margin-vertical-2">
                                <div className="column-medium-6">
                                    <ValidatedSelect id="household-member-participant-mode"
                                        value={model.householdMemberParticipantMode}
                                        onChange={(e) => handleOnChange('householdMemberParticipantMode', e)}
                                        label="New Household Member Default"
                                        messages={[]}>
                                        <option value="defaultUnchecked">Default to not a full participant</option>
                                        <option value="defaultChecked">Default to a full participant</option>
                                        <option value="alwaysHouseholdMember">Always keep as a household member</option>
                                        <option value="alwaysFullParticipant">Always make a full participant</option>
                                    </ValidatedSelect>
                                </div>
                                <div className="column-medium-6">
                                    <p className="text-sub margin-top-4">
                                        {model.householdMemberParticipantMode === 'defaultUnchecked' && 'The “Create as participant” option is available and starts unchecked.'}
                                        {model.householdMemberParticipantMode === 'defaultChecked' && 'The “Create as participant” option is available and starts checked.'}
                                        {model.householdMemberParticipantMode === 'alwaysHouseholdMember' && 'The “Create as participant” option is hidden; new household members are kept as household members.'}
                                        {model.householdMemberParticipantMode === 'alwaysFullParticipant' && 'The “Create as participant” option is hidden; new household members are always created as full participants.'}
                                    </p>
                                </div>
                            </div>
                            </>}
                            <div className="row justify-content-end margin-top-4">
                                <div className="column-auto">
                                    <Button id="submit-button"
                                        type="submit" 
                                        text="Save" 
                                        loading={submitting} 
                                        disabled={submitting} />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {demographicsOrgSettingsEnabled && <>
                <DemographicsPreviewModal
                    visible={showDemographicsPreview}
                    onClose={() => setShowDemographicsPreview(false)}
                    config={demographicsPreviewConfig}
                />
                <DemographicsOptionsInfoModal
                    visible={showDemographicsOptionsInfo}
                    onClose={() => setShowDemographicsOptionsInfo(false)}
                />
            </>}
        </section>
    );
};

export default EditOrganization;
